/**
 * Selects `n` random elements from the given `sequence`.
 *
 * @param {T[]} sequence - The array of elements to choose from.
 * @param {number} n - The number of elements to select.
 * @returns {T[]} - An array containing `n` random elements from the `sequence`.
 */
export function chooseRandomN<T>(sequence: T[], n: number): T[] {
  const shuffled = sequence.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, n);
}

/**
 * Generates a sequence of characters with a specified length,
 * where two digits are randomly inserted at a given distance
 * from each other.
 *
 * @param {number} length - The length of the sequence to generate.
 * @param {number} digit_distance - The distance between the two
 *     randomly inserted digits.
 * @param {number} digit_start - The index at which the first
 *     randomly inserted digit should appear.
 * @return {{ sequence: string; usedDigits: number[] }} An object containing the generated sequence
 *     and the array of used digits.
 */
export function generate_sequence(
  length: number,
  digit_distance: number,
  digit_start: number
): { sequence: string; usedDigits: number[] } {
  const alphabet = "abcdefghijklmnopqrstuvwxyz";
  const digits = Array.from(Array(10).keys());

  const chosen_letters = chooseRandomN(Array.from(alphabet), length - 2).join(
    ""
  );
  const chosen_digits = chooseRandomN(digits, 2);

  const sequence =
    chosen_letters.slice(0, digit_start) +
    chosen_digits[0] +
    chosen_letters.slice(digit_start, digit_start + digit_distance) +
    chosen_digits[1] +
    chosen_letters.slice(digit_start + digit_distance);

  return { sequence, usedDigits: chosen_digits };
}

/**
 * Sleeps for a specified number of milliseconds.
 *
 * @param {number} milliseconds - The number of milliseconds to sleep.
 * @return {Promise<void>} A promise that resolves after the specified number of milliseconds.
 */
export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
